import moment, { Moment } from 'moment-timezone';

interface InvoiceForAr {
  due_at?: string | null;
  unpaid_amount_in_micros: number;
}

interface CreditMemoForAr {
  credit_amount_in_micros: number;
}

interface ArSummaryInput {
  creditMemos: CreditMemoForAr[];
  invoices: InvoiceForAr[];
  startOfDay: Moment;
}

interface ArSummary {
  overdueBalanceInMicros: number;
  totalBalanceInMicros: number;
}

/**
 * Computes the AR card's display values from a customer's unpaid invoices and
 * available credit memos.
 *
 * - `totalBalanceInMicros` is clamped at 0 so a credit-heavy account doesn't
 *   show a negative "Total due."
 * - `overdueBalanceInMicros` is then capped at the (clamped) total so credits
 *   can't leave past-due higher than the total amount due.
 *
 * An invoice with `due_at < startOfDay` is overdue. Invoices with `null` /
 * missing `due_at` are counted toward the total only, not the overdue bucket.
 *
 * `startOfDay` is treated as read-only — moment objects are mutable, so don't
 * mutate the passed instance after calling.
 */
export const computeArSummary = ({
  creditMemos,
  invoices,
  startOfDay,
}: ArSummaryInput): ArSummary => {
  const creditTotal = creditMemos.reduce(
    (sum, memo) => sum + memo.credit_amount_in_micros,
    0,
  );
  const invoiceTotal = invoices.reduce(
    (sum, invoice) => sum + invoice.unpaid_amount_in_micros,
    0,
  );
  const overdueRaw = invoices
    .filter(
      (invoice) =>
        invoice.due_at && moment(invoice.due_at).isBefore(startOfDay),
    )
    .reduce((sum, invoice) => sum + invoice.unpaid_amount_in_micros, 0);

  const totalBalanceInMicros = Math.max(0, invoiceTotal - creditTotal);
  const overdueBalanceInMicros = Math.min(overdueRaw, totalBalanceInMicros);

  return { overdueBalanceInMicros, totalBalanceInMicros };
};
